import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import {
  AlertTriangle,
  Ban,
  CheckCircle,
  Copy,
  Delete,
  Download,
  Eye,
  EyeIcon,
  Filter,
  Instagram,
  Mail,
  MessageCircle,
  MoreHorizontal,
  Search,
  Send,
  Twitter,
  Users,
  XCircle,
} from "lucide-react";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { PlatformUser } from "../../App";
import useDebounce from "../../hooks/useDebounce";
import { ApiError, Refferal } from "../../services/api/auth/auth.type";
import {
  useGetTransactionsApiCallQuery,
  useLazyGetTransactionsApiCallQuery,
} from "../../services/api/transactions/transactions.api";
import { User } from "../../services/api/users/user.type";
import {
  useChangeUsersStatusApiCallMutation,
  useDeleteUsersStatusApiCallMutation,
  useGetUsersAnalyticsApiCallQuery,
  useAddKolMutation,
  useGetUsersApiCallQuery,
  useLazyGetUsersApiCallQuery,
} from "../../services/api/users/users.api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  useGetRefferalsApiCallQuery,
  useLazyGetRefferalsApiCallQuery,
} from "../../services/api/auth/auth.api";
import { TransactionsType } from "../../services/api/transactions/transactions.type";
import { DialogTrigger } from "@radix-ui/react-dialog";
import { set } from "react-hook-form";
import shortenAddress from "../../utils/shortenAddress";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useGetTokenPriceConversionApiCallQuery } from "../../services/api/campaigns/campaigns.api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { Crown } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Mock user data
const mockUsers: PlatformUser[] = [
  {
    id: "1",
    walletAddress: "0x1234567890abcdef1234567890abcdef12345678",
    joinedDate: "2024-01-15T10:30:00Z",
    linkedAccounts: {
      twitter: "@user1",
      telegram: "@user1_tg",
    },
    campaignsJoined: 3,
    status: "active",
    totalRewards: 450,
  },
  {
    id: "2",
    walletAddress: "0xabcdef1234567890abcdef1234567890abcdef12",
    joinedDate: "2024-01-16T14:20:00Z",
    linkedAccounts: {
      twitter: "@cryptouser2",
    },
    campaignsJoined: 2,
    status: "active",
    totalRewards: 320,
  },
  {
    id: "3",
    walletAddress: "0x9876543210fedcba9876543210fedcba98765432",
    joinedDate: "2024-01-17T09:15:00Z",
    linkedAccounts: {
      telegram: "@suspicious_user",
    },
    campaignsJoined: 1,
    status: "flagged",
    totalRewards: 100,
  },
  {
    id: "4",
    walletAddress: "0xfedcba9876543210fedcba9876543210fedcba98",
    joinedDate: "2024-01-18T16:45:00Z",
    linkedAccounts: {
      twitter: "@banned_user",
      telegram: "@banned_user_tg",
    },
    campaignsJoined: 5,
    status: "banned",
    totalRewards: 0,
  },
  {
    id: "5",
    walletAddress: "0x1111222233334444555566667777888899990000",
    joinedDate: "2024-01-19T11:30:00Z",
    linkedAccounts: {
      twitter: "@activeuser5",
      telegram: "@activeuser5_tg",
    },
    campaignsJoined: 4,
    status: "active",
    totalRewards: 675,
  },
];

// Mock user activity data
const mockUserActivity = [
  {
    id: "1",
    campaignName: "Project X Launch",
    taskType: "twitter_follow",
    taskDescription: "Follow @ProjectX_Official",
    status: "completed",
    completedDate: "2024-01-16T10:30:00Z",
    reward: 100,
  },
  {
    id: "2",
    campaignName: "Project X Launch",
    taskType: "twitter_retweet",
    taskDescription: "Retweet launch announcement",
    status: "completed",
    completedDate: "2024-01-16T11:15:00Z",
    reward: 50,
  },
  {
    id: "3",
    campaignName: "Community Growth",
    taskType: "telegram_join",
    taskDescription: "Join community channel",
    status: "pending",
    completedDate: null,
    reward: 75,
  },
  {
    id: "4",
    campaignName: "Beta Testing",
    taskType: "twitter_like",
    taskDescription: "Like beta announcement",
    status: "failed",
    completedDate: null,
    reward: 25,
  },
];

const AddKolButton: React.FC<{ userId: string }> = ({ userId }) => {
  const [addKol, { isLoading }] = useAddKolMutation();

  const handleAddKol = async () => {
    try {
      const data = await addKol(userId).unwrap();
      toast.success(data.responseMessage, {
        icon: <Crown className="h-4 w-4" />,
      });
    } catch (error: any) {
      toast.error("Failed to promote user to KOL", {
        description: error?.data?.message || "Something went wrong",
      });
    }
  };

  return (
    <AlertDialogAction
      onClick={handleAddKol}
      disabled={isLoading}
      className="bg-purple-600 hover:bg-purple-700"
    >
      {isLoading ? "Promoting..." : "Confirm & Add as KOL"}
    </AlertDialogAction>
  );
};

const KolManagement: React.FC = () => {
  // use states

  const navigate = useNavigate();

  const [users, setUsers] = useState<PlatformUser[]>(mockUsers);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [campaignFilter, setCampaignFilter] = useState<string>("");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [selected, setSelected] = useState<Refferal | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const [activityScroll, setActivityScroll] = useState(false);

  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const [refferalsLists, setRefferalsLists] = useState<Refferal[]>([]);
  const [transactionsLists, setTransactionsLists] = useState<
    TransactionsType[]
  >([]);

  const [referralTransactions, setReferralTransactions] = useState<
    TransactionsType[]
  >([]);

  const [page, setPage] = useState(1);

  const [refferalsPage, setRefferalsPage] = useState(1);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const refferalRef = useRef<HTMLDivElement | null>(null);
  const transactionsRef = useRef<HTMLDivElement>(document.createElement("div"));

  const debouncedSearch = useDebounce(searchTerm, 500);

  // api call
  const {
    data: usersData,
    isLoading: isUsersLoading,
    isError,
  } = useGetUsersApiCallQuery({
    search: debouncedSearch || "",
    status: statusFilter === "all" ? "" : statusFilter || "",
    page: page,
    isKol: true,
    limit: 10,
  });

  const [getUsers] = useLazyGetUsersApiCallQuery();

  const { data: analayticsUsers } = useGetUsersAnalyticsApiCallQuery();
  // const { data: transactions } = useGetTransactionsApiCallQuery({
  //   transactionType: "CLAIMED",
  //   page: 1,
  //   limit: 10,
  // });

  // const { data: refferals } = useGetRefferalsApiCallQuery({
  //   page: 1,
  //   limit: 10,
  //   userId: selectedUser?._id || "",
  // });

  const [getTransactions, { data: transactions }] =
    useLazyGetTransactionsApiCallQuery();

  const [getRefferals, { data: refferals }] = useLazyGetRefferalsApiCallQuery();

  const [deleteUser] = useDeleteUsersStatusApiCallMutation();
  const [changeUserStatus] = useChangeUsersStatusApiCallMutation();

  const getStatusBadge = (status: string) => {
    const colors = {
      active: "bg-green-100 text-green-800",
      block: "bg-red-100 text-red-800",
    };

    return (
      <Badge className={colors[status.toLowerCase() as keyof typeof colors]}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.walletAddress.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.linkedAccounts.twitter
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      user.linkedAccounts.telegram
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || user.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const handleViewDetails = async (user: User) => {
    setSelectedUser(user);
    setShowUserDetails(true);

    try {
      const response = await getRefferals({
        userId: user._id,
        page: refferalsPage,
        limit: 10,
      }).unwrap();

      setRefferalsLists(response.result?.docs || []);
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      console.log(errorData);
      setRefferalsLists([]);
      // toast.error(errorData?.responseMessage || "Login failed");
    }
  };

  const handleViewTransactions = async (user: User) => {
    setActivityScroll(true);
    console.log("aaya");
    try {
      const transactionsResponse = await getTransactions({
        userId: user._id,
        page: transactionsPage,
        limit: 4,
      }).unwrap();

      setTransactionsLists(transactionsResponse.result?.docs || []);
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      console.log(errorData);
      setTransactionsLists([]);
      // toast.error(errorData?.responseMessage || "Login failed");
    }
  };

  const formatWalletAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const handleCopy = (address: any) => {
    if (address) {
      navigator.clipboard.writeText(address);
      alert("Wallet address copied!");
    }
  };
  const getTaskStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "failed":
        return <XCircle className="h-4 w-4 text-red-600" />;
      case "pending":
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      default:
        return null;
    }
  };

  const handleChangeStatus = async (userId: string) => {
    try {
      const data = await changeUserStatus({
        userId,
      }).unwrap();

      if (data.responseCode === 200) {
        toast.success(data.responseMessage);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(
        errorData?.responseMessage || "Error changing campaign status"
      );
    }
  };

  const confirmDelete = async () => {
    setIsDeleteLoading(true);
    try {
      const data = await deleteUser({
        userId: deleteTargetId as string,
      }).unwrap();

      if (data.responseCode === 200) {
        toast.success(data.responseMessage);
        setDeleteTargetId(null);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(errorData?.responseMessage || "Error deleting campaign");
    } finally {
      setIsDeleteLoading(false);
      setDeleteTargetId(null);
    }
  };

  const handleReferralSelect = async (activity: Refferal) => {
    setSelected(activity);
    try {
      const response = await getTransactions({
        userId: activity._id,
        page: 1,
        limit: 10,
        transactionType: "CLAIMED",
      }).unwrap();
      if (response?.responseCode === 200) {
        setReferralTransactions(response.result?.docs || []);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleActivityScroll = async () => {
    try {
      const response = await getTransactions({
        userId: selectedUser?._id || "",
        page: transactionsPage,
        limit: 5,
      }).unwrap();

      if (response?.responseCode === 200) {
        const newDocs = response?.result?.docs || [];

        setTransactionsLists((prev) => [...newDocs]);
      }
    } catch (error) {
      console.error("Error fetching more transactions:", error);
    }
  };

  useEffect(() => {
    if (selectedUser?._id) {
      handleActivityScroll();
    }
  }, [transactionsPage]);

  const exportCSV = async () => {
    try {
      setIsExporting(true);

      const response = await getUsers({
        limit: usersData?.result?.total,
      }).unwrap();

      // const data: Transaction[] = response?.result?.data || [];

      if (response.result?.docs.length === 0) {
        toast.info("No data available to export");
        return;
      }

      const exportData = response?.result?.docs?.map((user) => ({
        ID: user._id,
        Name: user.firstName,
        Email: user.email,
        "User Type": user.userType,
        Status: user.status,
        Balance: user.balance,
        "OTP Verified": user.otpVerified ? "Yes" : "No",
        "Google 2FA": user.google2FA ? "Enabled" : "Disabled",
        Permissions: user.permissions?.join(", ") || "-",
        "Low Balance Alert": user.lowBalanceAlert ? "Yes" : "No",
        "Campaign End Alert": user.campaignEndAlert ? "Yes" : "No",
        // "New Campaign Alert": user.newCampaignAlert ? "Yes" : "No",
        // "Claim Reminder": user.claimReminder ? "Yes" : "No",
        // "Social Updates": user.socialUpdates ? "Yes" : "No",
        // "Kol Status": user.isKol ? "Active" : "InActive",
        "Total Campaigns": user.totalCampaigns,
        "Total Claimed": user.totalClaimed,
        "Wallet Address": user.walletAddress || "-",
        "Created At": new Date(user.createdAt).toLocaleString(),
        "Updated At": new Date(user.updatedAt).toLocaleString(),
      }));

      // Convert JSON → worksheet
      const worksheet = XLSX.utils.json_to_sheet(exportData!);

      // Create workbook and add worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "User");

      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      saveAs(blob, `user__${Date.now()}.xlsx`);
      toast.success("Excel file exported successfully");
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = (fetchError?.data as ApiError) || {};
      toast.error(errorData?.responseMessage || "Error exporting file");
    } finally {
      setIsExporting(false);
    }
  };

  const getSocialMediaIcon = (platform: string) => {
    switch (platform) {
      case "Twitter":
        return <Twitter className="h-4 w-4" color="#1DA1F2" />;
      case "Telegram":
        return <Send className="h-4 w-4" />;

      case "Instagram":
        return <Instagram className="h-4 w-4" color="#E1306C" />;
      default:
        return null;
    }
  };

  const { data: tokenPriceData } = useGetTokenPriceConversionApiCallQuery();

  const getUSDTBalance = (walletBalance: number) => {
    const usdtPrice =
      tokenPriceData?.result?.data?.find((item: any) => item.coin === "usdt")
        ?.tokenPrice || 0;
    const balance = walletBalance * usdtPrice || 0;
    return balance;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            KOL Management
          </h1>
          <p className="text-gray-600 mt-1">
            Monitor and manage platform users
          </p>
        </div>

        <Button
          disabled={isExporting}
          variant="outline"
          className="cursor-pointer"
          onClick={exportCSV}
        >
          <Download className="h-4 w-4 mr-2" />
          Export Users
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-">
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="flex flex-col sm:flex-row gap-4 flex-1">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search by wallet, Twitter, or Telegram..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>

                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-48">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="BLOCK">Block</SelectItem>
                    {/* <SelectItem value="flagged">Flagged</SelectItem> */}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Linked Accounts</TableHead>
                    <TableHead>Campaigns</TableHead>
                    <TableHead>Total Rewards</TableHead>
                    <TableHead>Total Rewards USDT</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Joined Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isUsersLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="h-24 text-center">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : (
                    !isError &&
                    usersData?.result?.docs &&
                    usersData?.result?.docs?.length > 0 &&
                    usersData.result.docs.map((user) => (
                      <TableRow key={user._id}>
                        <TableCell>
                          <div className="full-w font-mono text-sm">
                            {formatWalletAddress(user.walletAddress)}
                            {user.walletAddress ? (
                              <Copy
                                className=" h-4 cursor-pointer text-gray-500 hover:text-black"
                                onClick={() => {
                                  navigator.clipboard.writeText(
                                    user.walletAddress
                                  );
                                  toast.success("Wallet address copied!");
                                }}
                              />
                            ) : (
                              <span>--</span>
                            )}{" "}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            {user?.allSOcialMedia?.map((media) => (
                              <span>{getSocialMediaIcon(media.platform)}</span>
                            ))}
                            {user?.email && <Mail className="h-4 w-4" />}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">
                            {user.totalCampaigns}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">
                            {user.totalClaimed
                              ? user.totalClaimed.toFixed(3)
                              : 0}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">
                            $
                            {user.totalClaimed
                              ? getUSDTBalance(user.totalClaimed).toFixed(3)
                              : 0}
                          </span>
                        </TableCell>

                        <TableCell>{getStatusBadge(user.status)}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {new Date(user.createdAt).toLocaleDateString()}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuSeparator />

                              <DropdownMenuItem
                                onClick={() => {
                                  navigate(
                                    `/dashboard/private-campaigns/${user._id}`
                                  );
                                }}
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>

              {usersData?.result && usersData.result.pages > 1 && (
                <div className="flex justify-end items-center mt-4 gap-3">
                  {/* ---------- Previous ---------- */}
                  <Button
                    variant="outline"
                    disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Previous
                  </Button>

                  {/* ---------- Page Indicator + Input ---------- */}
                  <div className="flex items-center gap-1 text-sm">
                    <span>Page</span>

                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className="w-12 border rounded text-center focus:outline-none focus:ring-2 focus:ring-primary"
                      value={page}
                      onChange={(e) => {
                        const raw = Number(e.target.value.trim());

                        setPage(raw);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter")
                          (e.target as HTMLInputElement).blur();
                      }}
                      onBlur={(e) => {
                        const raw = e.target.value.trim();
                        if (raw === "") return setPage(1);

                        const n = Number(raw);
                        const totalPages = usersData?.result?.pages || 1;
                        if (Number.isNaN(n) || n < 1) {
                          setPage(1);
                        } else if (n > totalPages) {
                          setPage(totalPages);
                        } else {
                          setPage(n);
                        }
                      }}
                      aria-label="Current page"
                    />

                    <span>of {usersData.result.pages}</span>
                  </div>

                  {/* ---------- Next ---------- */}
                  <Button
                    variant="outline"
                    disabled={page === usersData.result.pages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}

              {/* popup for delete confirmation  */}
            </div>

            {(isError || usersData?.result?.docs.length === 0) && (
              <div className="text-center py-12">
                <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No users found
                </h3>
                <p className="text-gray-500">
                  {searchTerm || statusFilter !== "all"
                    ? "Try adjusting your search or filter criteria"
                    : "No users have joined the platform yet"}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default KolManagement;
